import type * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type { GSplatSystemDirector, GSplatViewRetireReason } from '@/plugins/gsplat/GSplatSystem'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatAssetStore } from '@/plugins/gsplat/system/GSplatAssetStore'
import type { GSplatPickingService } from '@/plugins/gsplat/system/GSplatPickingService'
import type {
  EngineFrameContext,
  ViewFrameContext,
  ViewId,
  ViewSnapshot,
} from '@/shared/types/view'

interface GSplatViewLifecycleState {
  readonly generation: number
  readonly camera: THREE.Camera
  suspended: boolean
  arrayCameraReported: boolean
}

/** 对应旧 FrameUpdater；只推进共享 Director/Manager 与 per-View 生命周期。 */
export class GSplatFrameService {
  private readonly viewStates = new Map<ViewId, GSplatViewLifecycleState>()
  private nextViewGeneration = 1
  private lastFrameNumber = -1

  constructor(
    private readonly store: GSplatAssetStore,
    private readonly director: GSplatSystemDirector,
    private readonly picking: GSplatPickingService,
    private readonly adapter: Pick<GSplatGraphicsAdapter, 'backend'>
  ) {}

  getMaterial(viewId: ViewId, layerId: number): unknown | null {
    return this.director.getMaterial(viewId, layerId)
  }

  update(frame: EngineFrameContext): void {
    if (this.lastFrameNumber === frame.frameNumber) return
    this.syncViewLifecycle(frame.views)
    this.director.updateStreaming(frame, this.store.snapshot())
    this.lastFrameNumber = frame.frameNumber
  }

  prepare(frame: ViewFrameContext): void {
    if (frame.viewRect.width <= 0 || frame.viewRect.height <= 0) {
      this.suspendView(frame.viewId)
      return
    }

    let state = this.viewStates.get(frame.viewId)
    if (state && state.camera !== frame.camera) {
      this.retireView(frame.viewId, state, 'camera-replaced')
      state = undefined
    }
    if (!state) {
      state = {
        generation: this.nextViewGeneration++,
        camera: frame.camera,
        suspended: false,
        arrayCameraReported: false,
      }
      this.viewStates.set(frame.viewId, state)
    }

    if (this.adapter.backend === 'webgpu' && (frame.camera as THREE.ArrayCamera).isArrayCamera) {
      if (!state.arrayCameraReported) {
        state.arrayCameraReported = true
        throw new UnsupportedGraphicsCapabilityError(
          'gsplat-array-camera',
          this.adapter.backend,
          `viewId=${frame.viewId}`
        )
      }
      return
    }

    state.suspended = false
    const assets = this.store.snapshot()
    const lifecycle = Object.freeze({ generation: state.generation })
    this.director.prepareView(frame, assets, lifecycle)
    this.picking.prepareView(frame, assets, state.generation)
  }

  dispose(): void {
    let firstError: unknown
    for (const [viewId, state] of [...this.viewStates]) {
      try {
        this.retireView(viewId, state, 'system-dispose')
      } catch (error) {
        firstError ??= error
      }
    }
    if (firstError) throw firstError
  }

  private syncViewLifecycle(views: readonly ViewSnapshot[]): void {
    const snapshots = new Map(views.map((view) => [view.id, view]))
    for (const [viewId, state] of [...this.viewStates]) {
      const snapshot = snapshots.get(viewId)
      if (!snapshot) {
        this.retireView(viewId, state, 'removed')
        continue
      }
      if (!isRenderableView(snapshot) && !state.suspended) {
        this.director.suspendView(viewId, state.generation)
        state.suspended = true
      }
    }
  }

  private suspendView(viewId: ViewId): void {
    const state = this.viewStates.get(viewId)
    if (!state || state.suspended) return
    this.director.suspendView(viewId, state.generation)
    state.suspended = true
  }

  private retireView(
    viewId: ViewId,
    state: GSplatViewLifecycleState,
    reason: GSplatViewRetireReason
  ): void {
    if (this.viewStates.get(viewId) !== state) return
    this.viewStates.delete(viewId)
    let firstError: unknown
    try {
      this.director.retireView(viewId, state.generation, reason)
    } catch (error) {
      firstError = error
    }
    try {
      this.picking.cancelView(viewId, state.generation)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }
}

function isRenderableView(view: ViewSnapshot): boolean {
  return view.visible && view.rect.width > 0 && view.rect.height > 0
}
